import { useState } from 'react';
import { SiteSettings } from '@/hooks/useSiteSettings';

interface DelaySettingsEditorProps {
  settings: SiteSettings;
  onSave: (s: SiteSettings) => void;
  loading?: boolean;
}

export default function DelaySettingsEditor({ settings, onSave, loading }: DelaySettingsEditorProps) {
  const [form, setForm] = useState<SiteSettings>({ ...settings });
  const [saved, setSaved] = useState(false);

  const update = (field: keyof SiteSettings, value: number) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handleSave = () => {
    onSave(form);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleReset = () => {
    setForm({ ...settings });
    setSaved(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">
            Delay de Carregamento
          </h2>
          <p className="text-foreground-500 text-sm mt-1">
            Configure o tempo de espera artificial (delay) do site em segundos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-sm text-foreground-600 border border-background-300 rounded-md hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            Descartar
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className={`px-4 py-2 text-sm font-bold rounded-md transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 ${
              saved
                ? 'bg-green-500 text-white'
                : 'bg-primary-500 hover:bg-primary-600 text-white'
            }`}
          >
            {saved ? (
              <><i className="ri-checkbox-circle-line"></i> Salvo!</>
            ) : (
              <><i className="ri-save-line"></i> Salvar alterações</>
            )}
          </button>
        </div>
      </div>

      {/* Settings cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Initial Delay */}
        <div className="bg-background-50 border border-background-200 rounded-xl p-5 space-y-4">
          <h3 className="font-bold text-foreground-950 text-sm flex items-center gap-2">
            <i className="ri-time-line text-primary-500"></i>
            Delay de Abertura do Site
          </h3>
          <p className="text-foreground-500 text-xs leading-relaxed">
            Tempo de espera que o usuário vê o gatinho carregando quando acessa o site pela primeira vez.
          </p>
          <div>
            <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-2">
              Tempo (segundos)
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min={0}
                max={10}
                step={0.5}
                value={form.initialDelayMs / 1000}
                onChange={e => update('initialDelayMs', Math.round(parseFloat(e.target.value) * 1000))}
                className="flex-1 accent-primary-500 cursor-pointer"
              />
              <span className="text-foreground-950 font-bold text-sm min-w-[4rem] text-right">
                {(form.initialDelayMs / 1000).toFixed(1)}s
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-background-100 rounded-lg p-3">
            <i className="ri-loader-4-line text-primary-500 animate-spin text-lg"></i>
            <span className="text-foreground-600 text-xs">
              Preview: {form.initialDelayMs >= 1000 ? `${(form.initialDelayMs / 1000).toFixed(1)} segundos` : 'Sem delay'} de tela de carregamento
            </span>
          </div>
        </div>

        {/* Category Delay */}
        <div className="bg-background-50 border border-background-200 rounded-xl p-5 space-y-4">
          <h3 className="font-bold text-foreground-950 text-sm flex items-center gap-2">
            <i className="ri-price-tag-3-line text-primary-500"></i>
            Delay ao Trocar de Categoria
          </h3>
          <p className="text-foreground-500 text-xs leading-relaxed">
            Tempo de espera que o usuário vê o gatinho carregando ao navegar entre categorias diferentes.
          </p>
          <div>
            <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-2">
              Tempo (segundos)
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min={0}
                max={10}
                step={0.5}
                value={form.categoryDelayMs / 1000}
                onChange={e => update('categoryDelayMs', Math.round(parseFloat(e.target.value) * 1000))}
                className="flex-1 accent-primary-500 cursor-pointer"
              />
              <span className="text-foreground-950 font-bold text-sm min-w-[4rem] text-right">
                {(form.categoryDelayMs / 1000).toFixed(1)}s
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-background-100 rounded-lg p-3">
            <i className="ri-footprint-line text-primary-500 text-lg"></i>
            <span className="text-foreground-600 text-xs">
              Preview: {form.categoryDelayMs >= 1000 ? `${(form.categoryDelayMs / 1000).toFixed(1)} segundos` : 'Sem delay'} de tela de carregamento
            </span>
          </div>
        </div>
      </div>

      {/* Info box */}
      <div className="bg-accent-100 border border-accent-200 rounded-xl p-4 flex items-start gap-3">
        <i className="ri-information-line text-accent-500 text-lg flex-shrink-0 mt-0.5"></i>
        <div>
          <p className="text-accent-900 text-sm font-bold mb-1">Dica</p>
          <p className="text-accent-700 text-xs leading-relaxed">
            Defina como 0 para desativar o delay. O delay é proposital e serve para criar uma experiência de carregamento mais envolvente com o gatinho animado.
          </p>
        </div>
      </div>
    </div>
  );
}